import { z } from 'zod';
import { addressShape } from './address.js';
import { Verification } from './data-states.js';
import { ClientKind } from './intake.js';
import { listQueryShape, pageOf } from './lists.js';
import { PEP_NOT_STATED, PepStatement } from './pep.js';
import { ClientRole } from './client-roles.js';
import { CountryCode as Country, isoDay as day, isoInstant as instant } from './values.js';
import { IntakePurpose } from './mandates.js';
/**
 * Taking on a mandate (development paper §6.1, steps 2-5) and the due diligence of the GwG
 * (ADR-025) - as a frame without rules: Kanos records what was found, with its origin and how
 * far it was checked; which of it the law asks for in a case, the rules of the responsible
 * experts will say. Until then nothing is taken as required, and nothing as "not needed".
 */
const Name = z.string().trim().min(1).max(100);
// --- The intake of a mandate ---------------------------------------------------------------
/** `open` → `completed` | `cancelled`; the database allows no other transition. */
export const IntakeCaseStatus = z
    .enum(['open', 'completed', 'cancelled'])
    .meta({ id: 'IntakeCaseStatus' });
/** Started from an inquiry of the portal, or by the firm for someone who came another way. */
/** Who an intake is about, typed in by hand or brought along in a file. */
export const IntakePerson = z
    .object({
    kind: ClientKind,
    name: z.string().trim().min(2).max(200),
    email: z.email().max(254),
})
    .meta({ id: 'IntakePerson' });
/**
 * Whether to invite the person into the portal with the start, in the same transaction. Left
 * out, the firm's setting decides for a single start (ADR-056) - an import of many invites only
 * when asked; optional, so the contract asks nothing more of its callers.
 */
const inviteAtOnce = { invite: z.boolean().exactOptional() };
export const IntakeCaseInput = z
    .discriminatedUnion('source', [
    z.object({ source: z.literal('inquiry'), inquiryId: z.uuid(), ...inviteAtOnce }),
    IntakePerson.extend({ source: z.literal('manual'), ...inviteAtOnce }),
])
    .meta({ id: 'IntakeCaseInput' });
/** How many people one import brings in at most. */
export const INTAKE_IMPORT_MAX = 500;
/** A row of an import: the person, and the client number the firm's software knows it by. */
export const IntakeImportRow = IntakePerson.extend({
    /** E.g. the DATEV client number; kept with the intake, unique in the firm. */
    clientNumber: z.string().trim().min(1).max(50).nullable().default(null),
    /** The day the business relationship began, where the file says (ADR-080 § 1). */
    existingSince: day.nullable().default(null),
}).meta({ id: 'IntakeImportRow' });
/**
 * The firm's existing clients from a file (DATEV or any CSV): each row becomes an intake of its
 * own (ADR-037) - and, if the firm says so, is invited into the portal at once.
 */
export const IntakeCaseImport = z
    .object({
    rows: z.array(IntakeImportRow).min(1).max(INTAKE_IMPORT_MAX),
    ...inviteAtOnce,
})
    .meta({ id: 'IntakeCaseImport' });
/**
 * Why a row was not taken: its address or client number has an intake already, or it stands in
 * the file before. Rows count from 0, in the order sent.
 */
export const IntakeImportSkip = z
    .object({ row: z.int().nonnegative(), reason: z.enum(['exists', 'repeated']) })
    .meta({ id: 'IntakeImportSkip' });
export const IntakeCaseImportResult = z
    .object({
    started: z.int().nonnegative(),
    /** Of them invited into the portal. */
    invited: z.int().nonnegative(),
    skipped: z.array(IntakeImportSkip),
})
    .meta({ id: 'IntakeCaseImportResult' });
/**
 * Why an intake ended without a mandate (decided 2026-11-01): the firm declined it, or the
 * client withdrew - chosen by the firm when it ends the intake. Or the client's contract ended or
 * was withdrawn while it was open (`mandate_ended`, ADR-076): set by Kanos, never chosen. Kept
 * with the intake and in the audit log.
 */
export const IntakeCancelReason = z
    .enum(['declined', 'withdrawn', 'mandate_ended'])
    .meta({ id: 'IntakeCancelReason' });
/** The reasons the firm chooses from when it ends an intake: the contract's end is Kanos's. */
export const IntakeClosingCancelReason = IntakeCancelReason.exclude(['mandate_ended']).meta({
    id: 'IntakeClosingCancelReason',
});
export const IntakeCase = z
    .object({
    id: z.uuid(),
    kind: ClientKind,
    name: z.string(),
    email: z.string(),
    /** The client it is an intake of (ADR-048) - shared by an update or a new start. */
    clientId: z.uuid(),
    /** The client's number the firm's software knows it by (e.g. DATEV), if it has one. */
    clientNumber: z.string().nullable(),
    /** The inquiry it came from; `null` when started by hand, or once that inquiry is gone. */
    inquiryId: z.uuid().nullable(),
    status: IntakeCaseStatus,
    createdAt: instant,
    createdBy: z.string(),
    closedAt: instant.nullable(),
    closedBy: z.string().nullable(),
    /**
     * Who carried the client when it closed, by Keycloak `sub` (ADR-054); `null`: still open, or
     * closed before this was recorded.
     */
    responsiblesAtClose: z.array(z.string()).nullable(),
    /** Why it ended without a mandate; `null`: not ended so, or ended before a reason was kept. */
    cancelReason: IntakeCancelReason.nullable(),
    /** When the contracting party last sent its data from the portal (ADR-026); `null`: never. */
    submittedAt: instant.nullable(),
    /** The team that carries it (ADR-043); `null`: none, open to everyone of the firm. */
    teamId: z.uuid().nullable(),
    /** Contact and address as the firm keeps them (master data); `null`: not stated. */
    phone: z.string().nullable(),
    ...addressShape,
    /** When the client's mandate ended, as the firm set it (ADR-063); `null`: it goes on. */
    mandateEndedOn: day.nullable(),
    /**
     * The mandate ended with the client's contract (ADR-076): its end is the contract's, not the
     * firm's to change or take back.
     */
    mandateEndedByContract: z.boolean(),
    /**
     * The mandate - one cycle of the client's business relationship - it belongs to, and what it
     * is to it: its onboarding or an update (ADR-078 § 4); `null` until attached.
     */
    mandateId: z.uuid().nullable(),
    purpose: IntakePurpose.nullable(),
    /** The client's running mandate began before Kanos (ADR-080 § 1). */
    existingMandate: z.boolean(),
    /**
     * Restricted after a request for erasure (Art. 18, ADR-063): left out of every list, opened
     * only for the retention duty with a second factor - read only.
     */
    restricted: z.boolean(),
})
    .meta({ id: 'IntakeCase' });
export const INTAKE_CASE_SORT_FIELDS = ['createdAt', 'name', 'status'];
export const IntakeCaseQuery = z
    .object({ ...listQueryShape, status: IntakeCaseStatus.optional() })
    .meta({ id: 'IntakeCaseQuery' });
export const IntakeCasePage = pageOf(IntakeCase, 'IntakeCasePage');
/** The intakes of one client, the newest first (ADR-048). */
export const IntakeCaseList = z.array(IntakeCase).meta({ id: 'IntakeCaseList' });
export const IntakeCaseParams = z.object({ caseId: z.uuid() }).meta({ id: 'IntakeCaseParams' });
/** A person of the firm who carries an intake (ADR-028), by Keycloak `sub`. */
export const IntakeResponsibleParams = z
    .object({ caseId: z.uuid(), subject: z.uuid() })
    .meta({ id: 'IntakeResponsibleParams' });
/**
 * Who carries an intake, and what the viewer may do with it: open identity data and close it
 * (`manager`: its responsibles and the licensed professionals), name responsibles (`assigner`:
 * managers and administrators) - hints for the UI; the API checks every call itself.
 */
export const IntakeResponsibles = z
    .object({
    responsibles: z.array(z.string()),
    viewer: z.object({ manager: z.boolean(), assigner: z.boolean() }),
})
    .meta({ id: 'IntakeResponsibles' });
export const IntakeCaseClosing = z
    .object({
    outcome: z.enum(['completed', 'cancelled']),
    /**
     * With `cancelled`: why. Left out, the reason stays unknown - for the clients of the API
     * from before it; with `completed` it is refused (`request.invalid`).
     */
    cancelReason: IntakeClosingCancelReason.optional(),
    /**
     * Why it completes while a screening is unresolved - pending, without an answer, or none
     * yet: required then, kept with the closing in the audit log (decided 2026-10-31). Unknown
     * is not no: the firm says why it completes anyway, the monitoring screens again.
     */
    unscreenedReason: z.string().trim().min(20).max(2000).optional(),
})
    .meta({ id: 'IntakeCaseClosing' });
// --- Beneficial owners (§ 3 GwG) -------------------------------------------------------------
/**
 * Why a person is a beneficial owner: holding shares, holding voting rights, controlling
 * otherwise - or `fictitious`, the legal representative where no one else is found.
 */
export const BeneficialInterest = z
    .enum(['ownership', 'voting_rights', 'control', 'fictitious'])
    .meta({ id: 'BeneficialInterest' });
/** Where the statement comes from: the client, the firm's own finding, or a register. */
export const OwnerSource = z.enum(['client', 'firm', 'registry']).meta({ id: 'OwnerSource' });
export const BeneficialOwnerInput = z
    .object({
    givenName: Name,
    familyName: Name,
    birthDate: day.nullable(),
    nationality: Country.nullable(),
    residenceCountry: Country.nullable(),
    interest: BeneficialInterest,
    /** Share or voting rights in hundredths of a per cent (25 % = 2500); `null`: not stated. */
    shareBasisPoints: z.int().min(0).max(10_000).nullable(),
    /** Politically exposed (ADR-029); left out: not stated. */
    pep: PepStatement.default(PEP_NOT_STATED),
    source: OwnerSource,
    verification: Verification,
    /** A known person of the client (ADR-051); left out, found by name and birth date or new. */
    personId: z.uuid().exactOptional(),
})
    .meta({ id: 'BeneficialOwnerInput' });
export const BeneficialOwner = BeneficialOwnerInput.extend({
    id: z.uuid(),
    /** The person around the client it is about (ADR-051). */
    personId: z.uuid(),
    recordedAt: instant,
    recordedBy: z.string(),
    /** Taken from a register: which data of which date (`company.info:register:<cin>@<date>`). */
    evidenceRef: z.string().nullable(),
}).meta({ id: 'BeneficialOwner' });
export const BeneficialOwnerList = z.array(BeneficialOwner).meta({ id: 'BeneficialOwnerList' });
export const BeneficialOwnerParams = z
    .object({ caseId: z.uuid(), ownerId: z.uuid() })
    .meta({ id: 'BeneficialOwnerParams' });
// --- Identification (§§ 11-13 GwG) -----------------------------------------------------------
/** How a person was identified; only `in_person` until a provider is chosen (ADR-025). */
export const IdentificationMethod = z
    .enum(['in_person', 'eid', 'video', 'eudi_wallet'])
    .meta({ id: 'IdentificationMethod' });
/** Whom: the contracting party itself, a person acting for it, or a beneficial owner. */
export const IdentifiedRole = z
    .enum(['contracting_party', 'representative', 'beneficial_owner'])
    .meta({ id: 'IdentifiedRole' });
export const IdentityDocumentType = z
    .enum(['id_card', 'passport', 'residence_permit', 'other'])
    .meta({ id: 'IdentityDocumentType' });
/**
 * What the document says - identity data: kept encrypted under a key of its own, read only for
 * a stated purpose, every reading audited (ADR-008, AGENTS.md).
 */
export const IdentityData = z
    .object({
    givenName: Name,
    familyName: Name,
    birthDate: day,
    birthPlace: z.string().trim().min(1).max(100),
    nationality: Country,
    address: z.string().trim().min(5).max(300),
    documentNumber: z.string().trim().min(4).max(40),
    issuingAuthority: z.string().trim().min(2).max(200),
    validUntil: day,
})
    .meta({ id: 'IdentityData' });
export const IdentificationInput = z
    .object({
    role: IdentifiedRole,
    /** With `beneficial_owner`: which one. */
    beneficialOwnerId: z.uuid().nullable(),
    documentType: IdentityDocumentType,
    /** The check itself: the document was there, genuine and matching the person. */
    outcome: z.enum(['verified', 'rejected']),
    data: IdentityData,
})
    .meta({ id: 'IdentificationInput' });
/**
 * An identification from before Kanos, recorded later for an existing mandate (ADR-080 § 2): the
 * day and way it was done then; the person recording it confirms it. Only one that succeeded.
 */
export const PriorIdentificationInput = IdentificationInput.omit({ outcome: true })
    .extend({ method: IdentificationMethod, identifiedOn: day })
    .meta({ id: 'PriorIdentificationInput' });
/** An identification as listed - without the identity data. */
export const Identification = z
    .object({
    id: z.uuid(),
    role: IdentifiedRole,
    beneficialOwnerId: z.uuid().nullable(),
    method: IdentificationMethod,
    documentType: IdentityDocumentType,
    outcome: z.enum(['verified', 'rejected']),
    identifiedAt: instant,
    /** Who checked, by the account's `sub` - with a fresh second factor. */
    identifiedBy: z.string(),
    /**
     * Done before Kanos (ADR-080 § 2): when it was recorded here. `identifiedAt` is then the day
     * of then and `identifiedBy` who confirms it. `null`: done in Kanos.
     */
    priorRecordedAt: instant.nullable(),
    /** Images of the document, kept apart from the check (development paper §6.1 No. 5). */
    images: z.int().nonnegative(),
})
    .meta({ id: 'Identification' });
export const IdentificationList = z.array(Identification).meta({ id: 'IdentificationList' });
export const IdentificationParams = z
    .object({ caseId: z.uuid(), identificationId: z.uuid() })
    .meta({ id: 'IdentificationParams' });
/** Why identity data is opened - stands in the audit log with every reading. */
export const IdentityPurposeQuery = z
    .object({ purpose: z.string().trim().min(5).max(200) })
    .meta({ id: 'IdentityPurposeQuery' });
export const IdentityImageSide = z.enum(['front', 'back']).meta({ id: 'IdentityImageSide' });
export const IdentityImageQuery = z
    .object({ side: IdentityImageSide })
    .meta({ id: 'IdentityImageQuery' });
/** A side of the document, and why its image is opened - for the audit log. */
export const IdentityImageReadQuery = z
    .object({ side: IdentityImageSide, purpose: z.string().trim().min(5).max(200) })
    .meta({ id: 'IdentityImageReadQuery' });
// --- Risk assessment (§ 10 GwG) --------------------------------------------------------------
export const RiskLevel = z.enum(['low', 'normal', 'high']).meta({ id: 'RiskLevel' });
export const riskLevelLabel = {
    low: 'Gering',
    normal: 'Normal',
    high: 'Hoch',
};
export const RiskAssessmentInput = z
    .object({
    level: RiskLevel,
    /** The professional's reasons, in their words - the record a supervisor reads. */
    reasoning: z.string().trim().min(20).max(4000),
})
    .meta({ id: 'RiskAssessmentInput' });
/**
 * A risk assessment: by a licensed professional, by hand (`manual`) until the rules of the
 * responsible experts propose one - the decision stays a person's.
 */
export const RiskAssessment = RiskAssessmentInput.extend({
    id: z.uuid(),
    origin: z.enum(['manual']),
    assessedAt: instant,
    assessedBy: z.string(),
}).meta({ id: 'RiskAssessment' });
/** Newest first; the first is the one in force. */
export const RiskAssessmentList = z.array(RiskAssessment).meta({ id: 'RiskAssessmentList' });
export const intakeCaseStatusLabel = {
    open: 'Offen',
    completed: 'Abgeschlossen',
    cancelled: 'Beendet ohne Mandat',
};
export const intakeCancelReasonLabel = {
    declined: 'Mandat abgelehnt',
    withdrawn: 'Mandant hat abgesagt',
    mandate_ended: 'Mandat beendet',
};
/** The status as a person reads it: an intake ended without a mandate says why, once known. */
export const intakeStatusText = (intake) => intake.status === 'cancelled' && intake.cancelReason
    ? intakeCancelReasonLabel[intake.cancelReason]
    : intakeCaseStatusLabel[intake.status];
export const beneficialInterestLabel = {
    ownership: 'Kapitalanteile',
    voting_rights: 'Stimmrechte',
    control: 'Kontrolle auf andere Weise',
    fictitious: 'Gesetzlicher Vertreter, weil sonst niemand feststellbar ist',
};
const share = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });
/** A share in hundredths of a per cent, the German way: 2500 → „25 %“, 3333 → „33,33 %“. */
export const formatShare = (basisPoints) => `${share.format(basisPoints / 100)} %`;
/** Who stated something - of an owner and of the contracting party alike (`PartySource`). */
export const ownerSourceLabel = {
    client: 'Mandantschaft',
    firm: 'Kanzlei',
    registry: 'Register',
};
export const identifiedRoleLabel = {
    contracting_party: 'Vertragspartner',
    representative: 'Vertretungsberechtigte Person',
    beneficial_owner: 'Wirtschaftlich Berechtigter',
};
export const identityDocumentTypeLabel = {
    id_card: 'Personalausweis',
    passport: 'Reisepass',
    residence_permit: 'Aufenthaltstitel',
    other: 'Anderes amtliches Dokument',
};
export const identificationMethodLabel = {
    in_person: 'Vor Ort',
    eid: 'Online-Ausweis',
    video: 'Video-Ident',
    eudi_wallet: 'EUDI-Wallet',
};
/**
 * The record of an intake's due diligence (§ 8 GwG): made once the intake is closed - what was
 * stated, checked, screened, decided and assessed, by whom and when - as PDF/A, kept unchanged
 * with its SHA-256 in the audit log. The identity data stay sealed in Kanos; the record names
 * which identifications there are.
 */
export const GwgRecord = z
    .object({
    id: z.uuid(),
    caseId: z.uuid(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    byteSize: z.int().positive(),
    createdAt: instant,
    createdBy: z.string(),
    /** The record it is an addendum to (ADR-050): the last of the client's earlier intakes. */
    predecessorId: z.uuid().nullable(),
})
    .meta({ id: 'GwgRecord' });
/** The record of an intake, or `null` while none was made. */
export const GwgRecordState = z
    .object({ record: GwgRecord.nullable() })
    .meta({ id: 'GwgRecordState' });
/** What an update round took over from the client's last completed intake (ADR-050). */
export const CarryOverResult = z
    .object({
    /** The intake it came from. */
    from: z.uuid(),
    /** Whether the contracting party's statement was taken over. */
    party: z.boolean(),
    /** How many beneficial owners were taken over. */
    owners: z.int().nonnegative(),
})
    .meta({ id: 'CarryOverResult' });
/** A person as a beneficial owner in one of the client's intakes (ADR-051). */
export const ClientPersonOwnership = z
    .object({
    ownerId: z.uuid(),
    caseId: z.uuid(),
    interest: BeneficialInterest,
    shareBasisPoints: z.int().nullable(),
    verification: Verification,
})
    .meta({ id: 'ClientPersonOwnership' });
/** A representative's identification linked to the person when it was recorded (ADR-052). */
export const ClientPersonRepresentation = z
    .object({
    identificationId: z.uuid(),
    caseId: z.uuid(),
    outcome: z.enum(['verified', 'rejected']),
    identifiedAt: instant,
    documentType: IdentityDocumentType,
})
    .meta({ id: 'ClientPersonRepresentation' });
/** Representatives' identifications of a client no person is linked to yet (ADR-052). */
export const ClientPersonRepresentationList = z
    .array(ClientPersonRepresentation)
    .meta({ id: 'ClientPersonRepresentationList' });
/** A representative's identification the firm links to a known person by hand (ADR-052). */
export const ClientPersonLinkInput = z
    .object({ identificationId: z.uuid() })
    .meta({ id: 'ClientPersonLinkInput' });
/**
 * One person around a client (ADR-051): its roles, where it is a beneficial owner, and whether it
 * was identified - one human, wherever it is named.
 */
export const ClientPerson = z
    .object({
    id: z.uuid(),
    /** As its newest statement names it. */
    name: z.string(),
    birthDate: day.nullable(),
    roles: z.array(ClientRole),
    ownerships: z.array(ClientPersonOwnership),
    /** Its identifications as a representative of the client (ADR-052). */
    representations: z.array(ClientPersonRepresentation),
    /** Identified - as beneficial owner or representative - in any of the client's intakes. */
    identified: z.boolean(),
})
    .meta({ id: 'ClientPerson' });
export const ClientPersonList = z.array(ClientPerson).meta({ id: 'ClientPersonList' });
/** What happened since an intake completed that asks for a new risk assessment (#227, ADR-062). */
export const ReassessmentReason = z
    .enum(['new-managing-director', 'new-shareholder', 'hit-confirmed', 'seat-abroad'])
    .meta({ id: 'ReassessmentReason' });
export const reassessmentReasonLabel = {
    'new-managing-director': 'neue Geschäftsführung',
    'new-shareholder': 'neuer Gesellschafter über 25 %',
    'hit-confirmed': 'bestätigter Treffer im Abgleich',
    'seat-abroad': 'Sitz im Ausland',
};
/**
 * Whether a completed intake needs a new risk assessment, by the rule `GWG-RISIKO-NEU-01`:
 * `required` `null` - open, a new shareholder's share unknown; never „no“ for it.
 */
export const Reassessment = z
    .object({
    required: z.boolean().nullable(),
    reasons: z.array(ReassessmentReason),
    ruleId: z.string(),
    ruleVersion: z.string(),
})
    .meta({ id: 'Reassessment' });
/** Whether a role is held on a day (`YYYY-MM-DD`); a bound not known holds on. */
export const roleHeldOn = (role, day) => (!role.validFrom || role.validFrom <= day) && (!role.validUntil || role.validUntil >= day);
/**
 * Who represents the client on a day - its managing directors - and was identified in none of
 * its intakes (ADR-062, decided 2026-10-31 in #227): to be identified; an earlier identification
 * of the same person, in another role or intake, counts.
 */
export const unidentifiedRepresentatives = (people, day) => people.filter((p) => !p.identified && p.roles.some((r) => r.kind === 'managing_director' && roleHeldOn(r, day)));
/** A person around the client of an intake (ADR-051). */
export const ClientPersonParams = z
    .object({ caseId: z.uuid(), personId: z.uuid() })
    .meta({ id: 'ClientPersonParams' });
/** Two persons the firm knows to be one human (ADR-052): this one goes into `into`. */
export const ClientPersonMergeInput = z
    .object({ into: z.uuid() })
    .meta({ id: 'ClientPersonMergeInput' });
const entryIds = z.array(z.uuid()).max(100);
/**
 * Entries of a person that are about another human (ADR-052): its current roles, beneficial
 * owners and representatives' identifications, each with what it replaced - to a new person.
 */
export const ClientPersonSplitInput = z
    .object({ roleIds: entryIds, ownerIds: entryIds, identificationIds: entryIds })
    .refine((e) => e.roleIds.length + e.ownerIds.length + e.identificationIds.length > 0, {
    message: 'Name at least one entry.',
})
    .meta({ id: 'ClientPersonSplitInput' });
//# sourceMappingURL=aml.js.map